import React from 'react';
import { Hourglass } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { formatCurrency } from '../../lib/utils';

const TYPES = [['20ft', "20' std"], ['40ft', "40' std"], ['40ft_hc', "40' HC"], ['20ft_reefer', "20' reefer"], ['40ft_reefer', "40' reefer"]];

function DayField({ label, hint, value, onChange, testId }) {
  return (
    <div className="p-3 bg-gray-50 rounded-xl">
      <Label className="text-gray-700 font-medium text-sm">{label}</Label>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <Input type="number" min="0" step="1" value={value ?? ''} onChange={function (e) { onChange(parseInt(e.target.value, 10) || 0); }} className="h-10 rounded-lg sv-mono w-28" data-testid={testId} />
    </div>
  );
}

function RateRow({ label, table, onChange, testId }) {
  return (
    <div className="grid grid-cols-[140px_repeat(5,minmax(0,1fr))] gap-2 items-center">
      <span className="text-sm text-gray-700">{label}</span>
      {TYPES.map(function (t) {
        return (
          <div key={t[0]} className="flex items-center gap-1"><span className="text-gray-400 text-xs">$</span>
            <Input type="number" min="0" step="5" value={table[t[0]] ?? ''} onChange={function (e) { onChange({ ...table, [t[0]]: parseFloat(e.target.value) || 0 }); }} className="h-9 rounded-lg text-right sv-mono px-2" data-testid={testId + '-' + t[0]} />
          </div>
        );
      })}
    </div>
  );
}

// Bridge › Pricing: free time and per-day demurrage / detention tariff by container type.
export function DemurragePanel({ dnd, onChange }) {
  function set(key, value) { onChange({ ...dnd, [key]: value }); }
  const sampleDays = 9, over = Math.max(0, sampleDays - (dnd.demurrage_free_days || 0));
  const t1 = Math.min(over, dnd.demurrage_tier1_days || 0), t2 = Math.max(0, over - (dnd.demurrage_tier1_days || 0));
  const sample = t1 * (dnd.demurrage_tier1['40ft'] || 0) + t2 * (dnd.demurrage_tier2['40ft'] || 0);
  return (
    <div className="bg-white rounded-3xl shadow-card p-6 lg:col-span-2" data-testid="demurrage-panel">
      <h2 className="text-xl font-medium tracking-[-0.01em] text-[#0F1B26] mb-2 flex items-center gap-2"><Hourglass className="h-5 w-5 text-[#1F97CC]" /> Demurrage & Detention</h2>
      <p className="text-sm text-gray-500 mb-6">Free time and daily charges per container. Demurrage runs from discharge (or ETA) until gate-out; detention runs from gate-out until the empty is returned. Shown on arrival notices, the tracking page and Compass.</p>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          <DayField label="Demurrage free time" hint="Calendar days from discharge" value={dnd.demurrage_free_days} onChange={function (v) { set('demurrage_free_days', v); }} testId="dnd-demurrage-free-days" />
          <DayField label="First tariff tier length" hint="Days charged at tier 1 before tier 2 applies" value={dnd.demurrage_tier1_days} onChange={function (v) { set('demurrage_tier1_days', v); }} testId="dnd-demurrage-tier1-days" />
          <DayField label="Detention free time" hint="Calendar days from gate-out to empty return" value={dnd.detention_free_days} onChange={function (v) { set('detention_free_days', v); }} testId="dnd-detention-free-days" />
        </div>
        <div className="lg:col-span-2 space-y-3">
          <div className="grid grid-cols-[140px_repeat(5,minmax(0,1fr))] gap-2 text-xs uppercase tracking-[0.06em] text-gray-500"><span>USD per day</span>{TYPES.map(function (t) { return <span key={t[0]} className="text-right pr-1">{t[1]}</span>; })}</div>
          <RateRow label="Demurrage tier 1" table={dnd.demurrage_tier1} onChange={function (v) { set('demurrage_tier1', v); }} testId="dnd-tier1" />
          <RateRow label="Demurrage tier 2" table={dnd.demurrage_tier2} onChange={function (v) { set('demurrage_tier2', v); }} testId="dnd-tier2" />
          <RateRow label="Detention" table={dnd.detention_daily} onChange={function (v) { set('detention_daily', v); }} testId="dnd-detention" />
          <div className="p-4 bg-[#0F1B26] text-white rounded-xl text-sm mt-4" data-testid="dnd-sample">
            <p className="text-white/70 text-xs mb-1">Sample: one 40' collected {sampleDays} days after discharge</p>
            <div className="flex justify-between"><span>{over} chargeable day(s) · {t1} @ tier 1 + {t2} @ tier 2</span><span className="sv-mono text-blue-600">{formatCurrency(sample)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
